import React from "react";
import classNames from "classnames";

export interface SwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string | [string, string];
    description?: string;
    disabled?: boolean;
    size?: "sm" | "md" | "lg";
    color?: "primary" | "success" | "warning" | "danger";
    labelPosition?: "left" | "right";
    required?: boolean;
    id?: string;
    name?: string;
}

const sizeClasses = {
    sm: {
        container: "h-5 w-9",
        thumb: "h-4 w-4",
        translate: "translate-x-4"
    },
    md: {
        container: "h-6 w-12",
        thumb: "h-5 w-5",
        translate: "translate-x-6"
    },
    lg: {
        container: "h-7 w-12",
        thumb: "h-6 w-6",
        translate: "translate-x-5"
    }
};

const colorClasses = {
    primary: "bg-[#2d3a3f]",
    success: "bg-green-500",
    warning: "bg-yellow-500",
    danger: "bg-red-500"
};

export const Switch: React.FC<SwitchProps> = ({
    checked,
    onChange,
    label,
    description,
    disabled = false,
    size = "md",
    color = "primary",
    labelPosition = "left",
    required = false,
    id,
    name
}) => {
    const sizeConfig = sizeClasses[size];
    const activeColor = colorClasses[color];
    const inactiveColor = "bg-[#c9d1d4]";

    const switchElement = (
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            className={classNames(
                "inline-flex items-center rounded-full transition-colors ",
                sizeConfig.container,
                {
                    [activeColor]: checked && !disabled,
                    [inactiveColor]: !checked || disabled,
                    "opacity-50 cursor-not-allowed": disabled,
                    "cursor-pointer": !disabled,
                    "focus:ring-[#2d3a3f]/20": color === "primary",
                    "focus:ring-green-500/20": color === "success",
                    "focus:ring-yellow-500/20": color === "warning",
                    "focus:ring-red-500/20": color === "danger"
                }
            )}
            aria-pressed={checked}
            aria-labelledby={id ? `${id}-label` : undefined}
            aria-describedby={id && description ? `${id}-description` : undefined}
            id={id}
            name={name}
        >
            <span
                className={classNames(
                    "ml-0.5 inline-block transform rounded-full bg-white shadow transition-transform",
                    sizeConfig.thumb,
                    {
                        [sizeConfig.translate]: checked,
                        "translate-x-0": !checked
                    }
                )}
            />
        </button>
    );

    const labelElement = () => {
        if (!label) return null;
        return (
            (Array.isArray(label) ? label : [label]).map((_label, index) => <div key={index} className={classNames("flex flex-col")}>
                <label
                    htmlFor={id}
                    id={id ? `${id}-label` : undefined}
                    className={classNames(
                        "font-roboto text-[14px] font-medium cursor-pointer",
                        {
                            "text-[#2d3a3f]": !disabled,
                            "text-gray-400": disabled
                        }
                    )}
                >
                    {_label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {description && (
                    <p
                        id={id ? `${id}-description` : undefined}
                        className={classNames(
                            "text-[12px] mt-0.5",
                            {
                                "text-[#5a717a]": !disabled,
                                "text-gray-400": disabled
                            }
                        )}
                    >
                        {description}
                    </p>
                )}
            </div>)
    );
    };

    if (!label) {
        return switchElement;
    }

    return (
        <div className={classNames("flex items-center gap-3", {
            "justify-between": labelPosition === "left",
            "flex-row-reverse justify-between": labelPosition === "right"
        })}>
            {Array.isArray(label) ? labelElement()?.[0] : labelElement()}
            {switchElement}
            {Array.isArray(label) ? labelElement()?.[1] : null}
        </div>
    );
};
